import { next } from '@ember/runloop';

const VIEWPORT_GAP = 4;
const TOP_LAYER_CLASS = 'wysiwyg-editor-shell__top-layer-overlay';

interface FloatingOverlayAnchor {
  bottom: number;
  left: number;
  right: number;
  top: number;
}

interface FloatingOverlayOptions {
  gap?: number;
  horizontalAlignment?: 'center' | 'start';
  restoreFocusTo?: HTMLElement;
  verticalPlacementBoundary?: Pick<FloatingOverlayAnchor, 'bottom' | 'top'>;
}

type PopoverElement = HTMLElement & {
  showPopover?: () => void;
};

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum);
}

export default class FloatingOverlayManager {
  private activeElement?: HTMLElement;
  private listeners?: AbortController;

  show(
    element: HTMLElement,
    anchor: FloatingOverlayAnchor,
    layoutAnchor: HTMLElement,
    onLayoutChange: () => void,
    options: FloatingOverlayOptions = {}
  ) {
    this.listeners?.abort();
    this.listeners = undefined;
    this.activeElement = element;

    const popoverElement = element as PopoverElement;

    if (typeof popoverElement.showPopover !== 'function') {
      element.removeAttribute('popover');
      return;
    }

    element.classList.add(TOP_LAYER_CLASS);

    if (!element.matches(':popover-open')) {
      popoverElement.showPopover();
    }

    const overlayRect = element.getBoundingClientRect();
    const gap = options.gap ?? VIEWPORT_GAP;
    const preferredLeft =
      options.horizontalAlignment === 'center'
        ? (anchor.left + anchor.right - overlayRect.width) / 2
        : anchor.left;
    const preferredTop = anchor.bottom + gap;
    const maximumLeft = Math.max(0, window.innerWidth - overlayRect.width);
    const maximumTop = Math.max(
      VIEWPORT_GAP,
      window.innerHeight - overlayRect.height - VIEWPORT_GAP
    );
    const aboveTop = anchor.top - overlayRect.height - gap;
    const verticalPlacementBoundary =
      options.verticalPlacementBoundary &&
      options.verticalPlacementBoundary.bottom >
        options.verticalPlacementBoundary.top
        ? options.verticalPlacementBoundary
        : {
            bottom: window.innerHeight - VIEWPORT_GAP,
            top: VIEWPORT_GAP,
          };
    const availableAbove =
      anchor.top - Math.max(verticalPlacementBoundary.top, VIEWPORT_GAP) - gap;
    const availableBelow =
      Math.min(
        verticalPlacementBoundary.bottom,
        window.innerHeight - VIEWPORT_GAP
      ) -
      anchor.bottom -
      gap;
    const shouldPlaceAbove =
      overlayRect.height > availableBelow && availableAbove > availableBelow;
    const top = clamp(
      shouldPlaceAbove ? aboveTop : preferredTop,
      VIEWPORT_GAP,
      maximumTop
    );
    const left = clamp(preferredLeft, 0, maximumLeft);

    element.style.left = `${left}px`;
    element.style.top = `${top}px`;

    const listeners = new AbortController();

    this.listeners = listeners;

    next(() => {
      if (
        this.activeElement !== element ||
        this.listeners !== listeners ||
        listeners.signal.aborted
      ) {
        return;
      }

      const { signal } = listeners;
      const handleLayoutChange = () => {
        const shouldRestoreFocus = element.contains(document.activeElement);

        onLayoutChange();

        if (shouldRestoreFocus) {
          next(() => {
            if (options.restoreFocusTo?.isConnected) {
              options.restoreFocusTo.focus({ preventScroll: true });
            }
          });
        }
      };
      let ancestor: HTMLElement | null = layoutAnchor;

      while (ancestor && ancestor !== document.body) {
        ancestor.addEventListener('scroll', handleLayoutChange, { signal });
        ancestor = ancestor.parentElement;
      }

      window.addEventListener('scroll', handleLayoutChange, { signal });
      window.addEventListener('resize', handleLayoutChange, { signal });
    });
  }

  unregister(element: HTMLElement) {
    if (element !== this.activeElement) {
      return;
    }

    this.listeners?.abort();
    this.listeners = undefined;
    this.activeElement = undefined;
  }
}
